import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/outline';

/**
 * Reusable Accordion Component
 *
 * A flexible, accessible accordion component that matches the existing design patterns
 * Supports custom icons, colors, and animations with proper ARIA attributes
 *
 * @component
 * @example
 * <Accordion
 *   title="Edit Caption & Style"
 *   icon="✏️"
 *   defaultExpanded={true}
 * >
 *   <CaptionEditor />
 *   <StylePanel />
 * </Accordion>
 */
export const Accordion = ({
  title,
  icon,
  children,
  defaultExpanded = false,
  variant = 'default',
  className = '',
  onToggle,
  forceExpanded,
  headerClassName = '',
  contentClassName = '',
  iconComponent,
  rightContent,
  disabled = false
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  // Generate unique ID for this accordion instance to prevent collisions
  const uniqueIdRef = useRef(`accordion-${Math.random().toString(36).substr(2, 9)}`);

  // Handle forced expansion from parent
  useEffect(() => {
    if (forceExpanded !== undefined) {
      setIsExpanded(forceExpanded);
    }
  }, [forceExpanded]);

  // Measure content height for smooth animations
  useEffect(() => {
    if (!contentRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContentHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(contentRef.current);
    return () => resizeObserver.disconnect();
  }, []); // Remove isExpanded dependency to prevent creating new observer on each toggle

  const handleToggle = useCallback(() => {
    if (disabled) return;

    const newState = !isExpanded;
    setIsExpanded(newState);
    if (onToggle) {
      onToggle(newState);
    }
  }, [isExpanded, onToggle, disabled]);

  // Variant styles configuration
  const variantStyles = {
    default: {
      container: 'border border-gray-200 rounded-lg bg-gray-50',
      header: 'hover:bg-gray-100',
      icon: 'text-gray-900',
      title: 'text-gray-900',
      chevron: 'text-gray-500',
      border: 'border-gray-200'
    },
    danger: {
      container: 'border border-red-200 rounded-lg bg-red-50',
      header: 'hover:bg-red-100',
      icon: 'text-red-700',
      title: 'text-red-900',
      chevron: 'text-red-500',
      border: 'border-red-200'
    },
    warning: {
      container: 'border border-yellow-200 rounded-lg bg-yellow-50',
      header: 'hover:bg-yellow-100',
      icon: 'text-yellow-700',
      title: 'text-yellow-900',
      chevron: 'text-yellow-500',
      border: 'border-yellow-200'
    },
    info: {
      container: 'border border-blue-200 rounded-lg bg-blue-50',
      header: 'hover:bg-blue-100',
      icon: 'text-blue-700',
      title: 'text-blue-900',
      chevron: 'text-blue-500',
      border: 'border-blue-200'
    },
    neutral: {
      container: 'border border-gray-200 rounded-lg bg-white',
      header: 'hover:bg-gray-50',
      icon: 'text-gray-700',
      title: 'text-gray-700',
      chevron: 'text-gray-400',
      border: 'border-gray-200'
    }
  };

  const styles = variantStyles[variant] || variantStyles.default;
  const accordionId = uniqueIdRef.current;

  return (
    <div className={`${styles.container} ${className}`}>
      {/* Header - Always visible */}
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          w-full px-4 py-3 flex items-center justify-between
          ${styles.header} transition-colors rounded-t-lg
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${headerClassName}
        `}
        aria-expanded={isExpanded}
        aria-controls={accordionId}
        aria-disabled={disabled}
      >
        <div className="flex items-center gap-2">
          {/* Icon */}
          {iconComponent ? (
            <div className={styles.icon}>
              {iconComponent}
            </div>
          ) : icon ? (
            <span
              className={`text-lg ${styles.icon}`}
              role="img"
              aria-label={`${title} icon`}
            >
              {icon}
            </span>
          ) : null}

          {/* Title */}
          <span className={`font-medium ${styles.title}`}>
            {title}
          </span>
        </div>

        <div className="flex items-center gap-3">
          {/* Optional right content (badges, status, etc.) */}
          {rightContent && (
            <div className="flex items-center">
              {rightContent}
            </div>
          )}

          {/* Chevron */}
          <div className={styles.chevron}>
            {isExpanded ? (
              <ChevronUpIcon className="w-5 h-5" />
            ) : (
              <ChevronDownIcon className="w-5 h-5" />
            )}
          </div>
        </div>
      </button>

      {/* Expandable Content with smooth animation */}
      <div
        className={`
          overflow-hidden transition-all duration-300 ease-in-out
          ${isExpanded ? 'opacity-100' : 'opacity-0'}
        `}
        style={{
          maxHeight: isExpanded ? `${contentHeight + 32}px` : '0px',
          visibility: isExpanded ? 'visible' : 'hidden'
        }}
      >
        <div
          ref={contentRef}
          id={accordionId}
          className={`px-4 pb-4 ${isExpanded ? `border-t ${styles.border}` : ''} ${contentClassName}`}
        >
          {children}
        </div>
      </div>
    </div>
  );
};

Accordion.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string,
  iconComponent: PropTypes.node,
  children: PropTypes.node.isRequired,
  defaultExpanded: PropTypes.bool,
  forceExpanded: PropTypes.bool,
  variant: PropTypes.oneOf(['default', 'danger', 'warning', 'info', 'neutral']),
  className: PropTypes.string,
  headerClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  onToggle: PropTypes.func,
  rightContent: PropTypes.node,
  disabled: PropTypes.bool
};

/**
 * Accordion Group Component
 *
 * Manages multiple accordions with optional exclusive expansion
 * (only one accordion can be expanded at a time)
 */
export const AccordionGroup = ({
  children,
  exclusive = false,
  defaultExpandedIndex = null,
  className = ''
}) => {
  const [expandedIndex, setExpandedIndex] = useState(defaultExpandedIndex);

  const handleAccordionToggle = useCallback((index, isExpanded) => {
    if (exclusive) {
      setExpandedIndex(isExpanded ? index : null);
    }
  }, [exclusive]);

  const enhancedChildren = React.Children.map(children, (child, index) => {
    if (!React.isValidElement(child)) return child;

    if (exclusive) {
      return React.cloneElement(child, {
        forceExpanded: expandedIndex === index,
        onToggle: (isExpanded) => {
          handleAccordionToggle(index, isExpanded);
          if (child.props.onToggle) {
            child.props.onToggle(isExpanded);
          }
        }
      });
    }

    return child;
  });

  return (
    <div className={`space-y-4 ${className}`}>
      {enhancedChildren}
    </div>
  );
};

AccordionGroup.propTypes = {
  children: PropTypes.node.isRequired,
  exclusive: PropTypes.bool,
  defaultExpandedIndex: PropTypes.number,
  className: PropTypes.string
};

export default Accordion;